"use client";

/**
 * Thin Capacitor bridge for the Android shell (com.yatishara.studio).
 * Plugins are loaded from window.Capacitor after `npx cap sync` in mobile/.
 */

type ListenerHandle = { remove: () => Promise<void> };
type PluginMethod = (options?: Record<string, unknown>) => Promise<unknown>;
type CapacitorPlugin = Record<string, PluginMethod> & {
  addListener?: (
    eventName: string,
    listener: (event: Record<string, unknown>) => void,
  ) => Promise<ListenerHandle>;
};

declare global {
  interface Window {
    Capacitor?: {
      isNativePlatform?: () => boolean;
      getPlatform?: () => string;
      Plugins?: Record<string, CapacitorPlugin>;
    };
  }
}

export const STUDIO_ORIGIN = "https://studio.yatishara.com";
export const STUDIO_CUSTOM_SCHEME = "yatishara";
export const STUDIO_CUSTOM_HOST = "studio";

/** Wam returnUrl contract (Convex studioWamAppReturnUrl). */
export const WAM_RETURN_PATH_PREFIX = "/pay/done/";

export function isNativeAndroid() {
  return Boolean(
    typeof window !== "undefined" &&
      window.Capacitor?.isNativePlatform?.() &&
      window.Capacitor?.getPlatform?.() === "android",
  );
}

export function capacitorPlugin(name: string): CapacitorPlugin | null {
  if (typeof window === "undefined") return null;
  return window.Capacitor?.Plugins?.[name] ?? null;
}

/**
 * Map an App Link / custom-scheme URL to a Studio path for WebView navigation.
 * Accepts:
 * - https://studio.yatishara.com/pay/done/{id}/?...
 * - yatishara://studio/pay/done/{id}/
 * - yatishara://studio?path=/pay/done/{id}/
 */
export function resolveStudioPath(rawUrl: string | undefined | null): string | null {
  if (!rawUrl) return null;
  try {
    const url = new URL(rawUrl, STUDIO_ORIGIN);
    if (url.protocol === `${STUDIO_CUSTOM_SCHEME}:` && url.hostname === STUDIO_CUSTOM_HOST) {
      const explicitPath = url.searchParams.get("path");
      if (explicitPath?.startsWith("/")) return explicitPath;
      const path = `/${url.pathname.replace(/^\/+/, "")}${url.search}${url.hash}`;
      return path === "/" && !url.search && !url.hash ? "/" : path;
    }
    if (url.protocol !== "https:" || url.hostname !== "studio.yatishara.com") {
      return null;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

export function isWamReturnPath(path: string | null | undefined): boolean {
  if (!path) return false;
  try {
    const url = new URL(path, STUDIO_ORIGIN);
    return url.pathname.replace(/\/+$/, "").startsWith(WAM_RETURN_PATH_PREFIX.replace(/\/+$/, ""));
  } catch {
    return path.includes(WAM_RETURN_PATH_PREFIX);
  }
}

export function navigateToStudioPath(rawUrl: string | undefined | null): boolean {
  const path = resolveStudioPath(rawUrl);
  if (!path || typeof window === "undefined") return false;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (current === path) return true;
  window.location.assign(path);
  return true;
}

/**
 * Open Wam hosted checkout on the web.
 * The Capacitor Android shell must not open a Wam Custom Tab for paid goods.
 * Wam hosts must stay OUT of capacitor allowNavigation so payment UI is not inlined.
 */
export async function openWamCheckout(
  checkoutUrl: string,
): Promise<"native-browser" | "assign" | "blocked"> {
  const url = String(checkoutUrl || "").trim();
  if (!url) return "assign";
  if (isNativeAndroid()) return "blocked";

  if (typeof window !== "undefined") {
    window.location.assign(url);
  }
  return "assign";
}

export async function closeNativeBrowser() {
  const browser = capacitorPlugin("Browser");
  if (!browser?.close) return;
  try {
    await browser.close();
  } catch {
    // Custom Tab may already be gone after an App Link handoff.
  }
}
